export const translations = {
  en: {
    // Header
    headerTitle: 'SDK Integration Guide',
    
    // Navigation
    navWhatIs: 'What is SilentLoss?',
    navFireForget: 'Fire & Forget',
    navQuickStart: 'Quick Start',
    navSdkSetup: 'SDK Setup',
    navAuthentication: 'Authentication',
    navEventTypes: 'Event Types',
    navEndpoints: 'Endpoints',
    navJsonFormats: 'JSON Formats',
    navPaymentInitialized: 'Payment Initialized',
    navProviderRequested: 'Provider Requested',
    navProviderSuccess: 'Provider Success',
    navProviderFailed: 'Provider Failed',
    navOrderCreated: 'Order Created',
    navRevenueRecorded: 'Revenue Recorded',
    navRefundInitiated: 'Refund Initiated',
    navRefundCompleted: 'Refund Completed',
    navBatchEvents: 'Batch Events',
    navErrorHandling: 'Error Handling',
    navBestPractices: 'Best Practices',
    navTesting: 'Test & Debug',
    navExamples: 'Example Scenarios',
    navWebhookIntegration: 'Webhook Integration',
    navFaq: 'FAQ',
    navSdks: 'SDKs & Libraries',
    
    // What is SilentLoss Section
    whatIsTitle: 'What is SilentLoss?',
    whatIsDesc: 'SilentLoss is a platform that detects lost revenue in your payment processes:',
    problemTitle: 'PROBLEM: Lost Revenue',
    problemDesc1: 'Stripe said successful ✅ → Your order was not created ❌ → 💸 LOST REVENUE',
    problemWhy: 'Why does this happen?',
    problemReason1: 'Database timeout',
    problemReason2: 'Webhook received but not processed',
    problemReason3: 'Exception thrown but not caught',
    problemReason4: 'Race condition',
    problemReason5: 'Request lost during deployment',
    solutionDesc: 'SilentLoss SDK monitors every step of your payment flow and detects lost revenue.',
    
    // Fire & Forget Section
    fireForgetTitle: 'Fire & Forget Approach (IMPORTANT!)',
    fireForgetCritical: 'SDK events must be sent asynchronously. Your application should NOT WAIT for a response from the SilentLoss API.',
    fireForgetWhy: 'Why?',
    wrongSyncTitle: '❌ WRONG: Synchronous Sending',
    wrongSyncDesc1: 'User clicks "Pay" → Send SDK Event → Wait for Response → ... → Continue',
    wrongSyncDesc2: 'User is waiting here!',
    wrongSyncProblem1: 'If API is slow → User waits (bad UX)',
    wrongSyncProblem2: 'If API errors → Payment appears failed (WRONG!)',
    wrongSyncProblem3: 'If timeout → Your app may crash',
    rightAsyncTitle: '✅ CORRECT: Fire & Forget (Async)',
    rightAsyncDesc1: 'User clicks "Pay" → Add to Queue (< 1ms) → Continue',
    rightAsyncDesc2: '[Background Worker]',
    rightAsyncDesc3: 'Send to API + Retry',
    rightAsyncBenefit1: 'User never waits (< 1ms)',
    rightAsyncBenefit2: 'API error does NOT affect user',
    rightAsyncBenefit3: 'Retries happen in background',
    rightAsyncBenefit4: 'Your app NEVER crashes',
    basicRulesTitle: 'Basic Rules',
    ruleAsync: 'Send Async',
    ruleAsyncDesc: 'Add events to queue, don\'t wait for response',
    ruleSwallow: 'Swallow Errors',
    ruleSwallowDesc: 'API error should not crash your app',
    ruleBackgroundRetry: 'Background Retry',
    ruleBackgroundRetryDesc: 'Retries should not affect user',
    ruleLogging: 'Logging',
    ruleLoggingDesc: 'Log errors, review later',
    
    // Quick Start Section
    quickStartTitle: 'Quick Start',
    quickStartIntro: 'This documentation explains how to integrate the SDK to send payment events to the SilentLoss Platform.',
    
    featureFastTitle: 'Fast Integration',
    featureFastDesc: 'Complete your API integration in minutes',
    featureSecureTitle: 'Secure',
    featureSecureDesc: 'API key authentication and signature verification',
    featureRealtimeTitle: 'Real-time',
    featureRealtimeDesc: 'Instant risk analysis and fraud detection',
    
    step1Title: 'Get API Key',
    step1Desc: 'Create an API key from your SilentLoss Platform dashboard:',
    step1Path: 'Dashboard → Settings → API Keys → Create New Key',
    apiKeyFormat: 'API Key Format:',
    
    step2Title: 'Send First Event',
    
    step3Title: 'Track Payment Flow',
    step3Desc: 'Send an event to the relevant endpoint for each payment status change:',
    
    // Authentication Section
    authTitle: 'Authentication (API Key)',
    authUsageTitle: 'API Key Usage',
    authUsageDesc: 'The API key must be sent as a Bearer token in the Authorization header for all requests:',
    authTypesTitle: 'API Key Types',
    authIpWhitelistTitle: 'IP Whitelist (Optional)',
    authIpWhitelistDesc: 'You can add access restrictions from specific IP addresses for your API key for security:',
    
    // Table Headers
    tableEnvironment: 'Environment',
    tablePrefix: 'Prefix',
    tableRateLimit: 'Rate Limit',
    tableUsage: 'Usage',
    tableTest: 'Test',
    tableProduction: 'Production',
    tableDevTest: 'Development and test',
    tableProdEnv: 'Production environment',
    
    // Event Types Section
    eventTypesTitle: 'Event Types',
    eventTypesDesc: 'The platform supports 8 different payment events:',
    tableEvent: 'Event',
    tableEndpoint: 'Endpoint',
    tableWhenSent: 'When Sent',
    
    eventInitializedWhen: 'When payment is initiated (checkout page)',
    eventProviderRequestedWhen: 'When request is sent to payment provider',
    eventProviderSuccessWhen: 'When successful response is received from provider',
    eventProviderFailedWhen: 'When error is received from provider',
    eventOrderCreatedWhen: 'When order is saved to database',
    eventRevenueRecordedWhen: 'When revenue is recorded in accounting system',
    eventRefundInitiatedWhen: 'When refund is initiated',
    eventRefundCompletedWhen: 'When refund is completed',
    
    // Endpoints Section
    endpointsTitle: 'Endpoints',
    baseUrlTitle: 'Base URL',
    eventEndpointsTitle: 'Event Endpoints',
    batchEndpointTitle: 'Batch Endpoint',
    
    // JSON Formats Section
    jsonFormatsTitle: 'JSON Formats',
    jsonFormatsDesc: 'Required and optional fields for each event type are detailed below.',
    requiredFields: 'Required Fields',
    optionalFields: 'Optional Fields',
    fieldDescriptions: 'Field Descriptions',
    exampleRequest: 'Example Request',
    successResponse: 'Success Response',
    whenToSend: 'When:',
    
    // Payment Initialized
    paymentInitializedTitle: '1. Payment Initialized',
    paymentInitializedWhen: 'When user arrives at checkout page or clicks "Pay" button.',
    
    // Provider Requested
    providerRequestedTitle: '2. Provider Requested',
    providerRequestedWhen: 'When payment request is sent to payment provider (Stripe, Iyzico, etc.).',
    
    // Provider Success
    providerSuccessTitle: '3. Provider Success',
    providerSuccessWhen: 'When successful response is received from payment provider.',
    providerSuccessWarning: 'The provider_payment_id field is required. This field must contain the payment intent ID returned from the provider (e.g., pi_abc123 in Stripe, paymentId in Iyzico). This ID is critical for webhook matching.',
    
    // Provider Failed
    providerFailedTitle: '4. Provider Failed',
    providerFailedWhen: 'When error is received from payment provider.',
    
    // Order Created
    orderCreatedTitle: '5. Order Created',
    orderCreatedWhen: 'When order is saved to database (after successful payment).',
    
    // Revenue Recorded
    revenueRecordedTitle: '6. Revenue Recorded',
    revenueRecordedWhen: 'When revenue is recorded in accounting/accounting system.',
    
    // Refund Initiated
    refundInitiatedTitle: '7. Refund Initiated',
    refundInitiatedWhen: 'When refund process is initiated.',
    
    // Refund Completed
    refundCompletedTitle: '8. Refund Completed',
    refundCompletedWhen: 'When refund is completed and money is returned to customer.',
    
    // Batch Events Section
    batchEventsTitle: 'Batch Events',
    batchEventsDesc: 'Use the batch endpoint to send multiple events in a single request.',
    batchEventTypesMapping: 'Event Types Mapping',
    batchLimitsTitle: 'Limits:',
    batchLimit1: 'Maximum 100 events per batch',
    batchLimit2: 'Maximum payload size: 1MB',
    
    // Error Handling Section
    errorHandlingTitle: 'Error Handling',
    httpStatusCodesTitle: 'HTTP Status Codes',
    tableCode: 'Code',
    tableMeaning: 'Meaning',
    tableAction: 'Action',
    statusSuccess: 'Success',
    statusSuccessAction: 'Event recorded',
    statusBadRequest: 'Bad Request',
    statusBadRequestAction: 'JSON format or required field missing',
    statusUnauthorized: 'Unauthorized',
    statusUnauthorizedAction: 'API key invalid or missing',
    statusValidationError: 'Validation Error',
    statusValidationErrorAction: 'Data validation error',
    statusRateLimitExceeded: 'Rate Limit Exceeded',
    statusRateLimitExceededAction: 'Rate limit exceeded, retry',
    statusServerError: 'Server Error',
    statusServerErrorAction: 'Platform error, retry',
    errorResponseFormat: 'Error Response Format',
    commonErrorCodes: 'Common Error Codes',
    tableDescription: 'Description',
    tableSolution: 'Solution',
    errorInvalidApiKey: 'API key invalid',
    errorInvalidApiKeySolution: 'Check API key',
    errorValidation: 'Required field missing',
    errorValidationSolution: 'Check JSON',
    errorDuplicate: 'Event already sent',
    errorDuplicateSolution: 'Change idempotency key',
    errorRateLimit: 'Rate limit exceeded',
    errorRateLimitSolution: 'Wait 60 seconds',
    errorInvalidProvider: 'Unsupported provider',
    errorInvalidProviderSolution: 'Check provider list',
    errorInvalidCurrency: 'Invalid currency',
    errorInvalidCurrencySolution: 'Use ISO 4217 codes',
    retryStrategy: 'Retry Strategy',
    retryStrategyDesc: 'Retry mechanism implementation using Exponential Backoff:',
    
    // Best Practices Section
    bestPracticesTitle: 'Best Practices',
    bp1Title: 'Idempotency Keys',
    bp1Desc: 'Use a unique idempotency key for each event:',
    bp1Why: 'Prevents the same event from being sent twice accidentally.',
    bp1CodeComment: '// Example: init-order-12345-1704067200',
    bp2Title: 'Event Order',
    bp2Desc: 'Send events in the correct order:',
    bp2Why: 'Platform performs state machine validation. Wrong order → risk detection.',
    bp3Title: 'Timestamps',
    bp3Desc: 'Use timestamp or recorded_at in events (ISO 8601):',
    bp3Why: 'Required for async processing detection.',
    bp4Title: 'Amount Consistency',
    bp4Desc: 'Use the same amount in all events:',
    bp4Why: 'Amount mismatch → risk detection.',
    bp5Title: 'Timeout Settings',
    bp5Desc: 'Add timeout to API calls (recommended: 10 seconds):',
    bp6Title: 'Metadata Usage',
    bp6Desc: 'Add additional info for debugging:',
    why: 'Why?',
    
    // Testing Section
    testingTitle: 'Test and Debugging',
    testModeTitle: 'Test Mode',
    testModeDesc: 'Test without affecting real data using test API key:',
    testPaymentIdsTitle: 'Test Payment Intent IDs',
    testPaymentIdsDesc: 'Special IDs for testing:',
    testPatternSuccess: 'Always successful',
    testPatternFail: 'Always fails',
    testPatternTimeout: 'Timeout simulation',
    dashboardCheckTitle: 'Check in Dashboard',
    dashboardCheckDesc: 'You can see all events in the timeline.',
    
    // Webhook Integration Section
    webhookTitle: 'Webhook Integration & Multi-Tenant Security',
    webhookCritical: 'Each Organization Has a Unique Webhook URL',
    webhookDesc: 'SilentLoss is a multi-tenant platform. Each of your customers (organization) has their own unique webhook URL:',
    webhookUrlFormat: 'URL Format',
    securityLayersTitle: 'Security Layers',
    securityUrlIsolation: 'URL-Based Isolation',
    securityUrlIsolationDesc: 'Each org uses different endpoint',
    securityWebhookSecret: 'Webhook Secret',
    securityWebhookSecretDesc: 'Each org must have different secret',
    securitySignatureValidation: 'Signature Validation',
    securitySignatureValidationDesc: 'Every webhook from provider is verified',
    whyProviderPaymentId: 'Why is provider_payment_id Required?',
    whyProviderPaymentIdDesc: 'SilentLoss Platform processes both SDK events and provider webhooks. The provider_payment_id field is required to match these two sources:',
    webhookMatchingCode1: 'Your system:',
    webhookMatchingCode2: 'Stripe webhook:',
    webhookMatchingCode3: 'provider_payment_id is required to match these two!',
    stripeWebhookSetupTitle: 'Stripe Webhook Setup Example',
    stripeStep1Title: 'Add Webhook in Stripe Dashboard',
    stripeStep2Title: 'Save Secret to SilentLoss',
    
    // SDKs Section
    sdksTitle: 'SDKs & Libraries',
    
    // Footer
    footerSupport: 'Support',
    footerRateLimits: 'Rate Limits',
    footerSla: 'SLA',
    footerUptime: 'Uptime',
    footerResponseTime: 'Response Time',
    footerSupportResponse: 'Support Response',
    footerCopyright: 'SilentLoss Platform © 2026. All rights reserved.',
    
    // Common
    important: 'IMPORTANT',
    critical: 'CRITICAL',
    example: 'Example',
    type: 'Type',
    required: 'Required',
    optional: 'Optional',
    field: 'Field',
    uniquePaymentId: 'Unique payment ID',
    paymentAmount: 'Payment amount',
    currencyIso: 'Currency (ISO 4217)',
    paymentProvider: 'Payment provider',
    customerEmail: 'Customer email address',
    customerId: 'Customer ID',
    productServiceId: 'Product/service ID',
    productServiceName: 'Product/service name',
    additionalInfo: 'Additional information',
    correspondingEndpoint: 'Corresponding Endpoint',
    apiVersion: 'API Version',
    
    // SDK Setup Section
    sdkSetupTitle: 'SDK Setup (By Language)',
    sdkSetupDesc: 'Fire & Forget: All SDK examples work asynchronously. Your user never waits, your app never crashes.',
    sdkJsTitle: 'JavaScript / Node.js (Recommended)',
    sdkPhpTitle: 'PHP (with Laravel)',
    sdkPhpOption1: 'Option 1: Laravel Queue (Recommended)',
    sdkPhpOption2: 'Option 2: PHP-FPM (fastcgi_finish_request)',
    sdkPythonTitle: 'Python',
    sdkGoTitle: 'Go',
    sdkJavaTitle: 'Java',
    sdkCsharpTitle: 'C# / .NET',
    sdkRubyTitle: 'Ruby',
    sdkUsage: 'USAGE',
    sdkFireForgetNote: 'This line returns IMMEDIATELY, user never waits!',
    
    // Example Scenarios Section
    examplesTitle: 'Example Scenarios',
    examplesNote: 'Note: In all examples, the paymentSafety SDK client works with Fire & Forget approach. No await is used!',
    example1Title: 'Scenario 1: Successful Stripe Payment',
    example2Title: 'Scenario 2: Failed Payment',
    example3Title: 'Scenario 3: PHP Laravel Complete Example',
    
    // FAQ Section
    faqTitle: 'Frequently Asked Questions (FAQ)',
    faq1Q: 'Q: What happens if event sending fails?',
    faq1A: 'Events are retried in the background. Even if it fails, your payment process is NOT affected. Events are logged and can be sent manually later.',
    faq2Q: 'Q: What happens if rate limit is exceeded?',
    faq2A: 'You receive a 429 error. The background worker waits according to the Retry-After header and tries again. Your payment process is NOT affected.',
    faq3Q: 'Q: What if all retries fail?',
    faq3A: 'Log the event and save to a "dead letter queue". You can send it manually later. CRITICAL: Never stop your payment process!',
    faq4Q: 'Q: Can I use synchronous sending?',
    faq4A: 'NOT RECOMMENDED. Synchronous sending has these risks: If API is slow → user waits (bad UX), If API errors → payment appears failed (WRONG!), If timeout → your app may crash',
    faq5Q: 'Q: Which events are required?',
    faq5A: 'Minimum: payment.initialized and payment.provider_success. For full protection, send all events.',
    faq6Q: 'Q: Does event order matter?',
    faq6A: 'Yes! Wrong order = potential risk detection. Order: initialized → provider_requested → provider_success → order_created → revenue_recorded',
    
    // Updated Error Handling
    errorGoldenRule: 'GOLDEN RULE: SilentLoss API error should NEVER stop your payment process!',
    errorPaymentCritical: 'YOUR PAYMENT IS CRITICAL, ANALYTICS IS NOT',
    errorPaymentCriticalDesc: 'Even if this step fails, the order MUST be created!',
    errorDetectsNotCauses: 'SilentLoss detects lost revenue. It does NOT cause it.',
    yourApp: 'Your App',
    continueFlow: 'Continue',
    logContinue: 'Log, continue',
    backgroundRetry: 'Background retry',
    tableRetry: 'Retry?',
    yes: 'Yes',
    no: 'No',
  },
  
  tr: {
    // Header
    headerTitle: 'SDK Entegrasyon Rehberi',
    
    // Navigation
    navWhatIs: 'SilentLoss Nedir?',
    navFireForget: 'Fire & Forget',
    navQuickStart: 'Hızlı Başlangıç',
    navSdkSetup: 'SDK Kurulumu',
    navAuthentication: 'Kimlik Doğrulama',
    navEventTypes: 'Event Tipleri',
    navEndpoints: 'Endpoint\'ler',
    navJsonFormats: 'JSON Formatları',
    navPaymentInitialized: 'Ödeme Başlatıldı',
    navProviderRequested: 'Provider\'a İstek',
    navProviderSuccess: 'Provider Başarılı',
    navProviderFailed: 'Provider Başarısız',
    navOrderCreated: 'Sipariş Oluşturuldu',
    navRevenueRecorded: 'Gelir Kaydedildi',
    navRefundInitiated: 'İade Başlatıldı',
    navRefundCompleted: 'İade Tamamlandı',
    navBatchEvents: 'Toplu Eventler',
    navErrorHandling: 'Hata Yönetimi',
    navBestPractices: 'En İyi Pratikler',
    navTesting: 'Test ve Debug',
    navExamples: 'Örnek Senaryolar',
    navWebhookIntegration: 'Webhook Entegrasyonu',
    navFaq: 'SSS',
    navSdks: 'SDK\'lar ve Kütüphaneler',
    
    // What is SilentLoss Section
    whatIsTitle: 'SilentLoss Nedir?',
    whatIsDesc: 'SilentLoss, ödeme süreçlerinizdeki kayıp gelirleri tespit eden bir platformdur:',
    problemTitle: 'PROBLEM: Kayıp Gelirler',
    problemDesc1: 'Stripe başarılı dedi ✅ → Siparişiniz oluşmadı ❌ → 💸 KAYIP GELİR',
    problemWhy: 'Neden olur?',
    problemReason1: 'Database timeout',
    problemReason2: 'Webhook geldi ama işlenmedi',
    problemReason3: 'Exception fırladı ama catch edilmedi',
    problemReason4: 'Race condition',
    problemReason5: 'Deployment sırasında istek kayboldu',
    solutionDesc: 'SilentLoss SDK ödeme akışınızın her adımını izler ve kayıp gelirleri tespit eder.',
    
    // Fire & Forget Section
    fireForgetTitle: 'Fire & Forget Yaklaşımı (ÖNEMLİ!)',
    fireForgetCritical: 'SDK event\'leri asenkron gönderilmelidir. Uygulamanız SilentLoss API\'sinden yanıt BEKLEMEMELİDİR.',
    fireForgetWhy: 'Neden?',
    wrongSyncTitle: '❌ YANLIŞ: Senkron Gönderim',
    wrongSyncDesc1: 'Kullanıcı "Ödeme Yap" → SDK Event Gönder → Yanıt Bekle → ... → Devam Et',
    wrongSyncDesc2: 'Kullanıcı burada bekliyor!',
    wrongSyncProblem1: 'API yavaşsa → Kullanıcı bekler (kötü UX)',
    wrongSyncProblem2: 'API hata verirse → Ödeme başarısız görünür (YANLIŞ!)',
    wrongSyncProblem3: 'Timeout olursa → Uygulamanız çökebilir',
    rightAsyncTitle: '✅ DOĞRU: Fire & Forget (Asenkron)',
    rightAsyncDesc1: 'Kullanıcı "Ödeme Yap" → Queue\'ya Ekle (< 1ms) → Devam Et',
    rightAsyncDesc2: '[Arka Plan Worker]',
    rightAsyncDesc3: 'API\'ye Gönder + Retry',
    rightAsyncBenefit1: 'Kullanıcı hiç beklemez (< 1ms)',
    rightAsyncBenefit2: 'API hatası kullanıcıyı ETKİLEMEZ',
    rightAsyncBenefit3: 'Retry\'lar arka planda olur',
    rightAsyncBenefit4: 'Uygulamanız ASLA çökmez',
    basicRulesTitle: 'Temel Kurallar',
    ruleAsync: 'Asenkron Gönder',
    ruleAsyncDesc: 'Event\'leri queue\'ya ekle, yanıt bekleme',
    ruleSwallow: 'Hata Yutma',
    ruleSwallowDesc: 'API hatası uygulamanızı çökertmemeli',
    ruleBackgroundRetry: 'Arka Plan Retry',
    ruleBackgroundRetryDesc: 'Retry\'lar kullanıcıyı etkilememeli',
    ruleLogging: 'Loglama',
    ruleLoggingDesc: 'Hataları logla, sonra incele',
    
    // Quick Start Section
    quickStartTitle: 'Hızlı Başlangıç',
    quickStartIntro: 'Bu dokümantasyon, SilentLoss Platform\'a ödeme olaylarını göndermek için SDK entegrasyonunun nasıl yapılacağını açıklar.',
    
    featureFastTitle: 'Hızlı Entegrasyon',
    featureFastDesc: 'Dakikalar içinde API entegrasyonunuzu tamamlayın',
    featureSecureTitle: 'Güvenli',
    featureSecureDesc: 'API key authentication ve signature verification',
    featureRealtimeTitle: 'Gerçek Zamanlı',
    featureRealtimeDesc: 'Anında risk analizi ve fraud detection',
    
    step1Title: 'API Key Alın',
    step1Desc: 'SilentLoss Platform dashboard\'unuzdan bir API key oluşturun:',
    step1Path: 'Dashboard → Settings → API Keys → Create New Key',
    apiKeyFormat: 'API Key Formatı:',
    
    step2Title: 'İlk Event\'i Gönderin',
    
    step3Title: 'Payment Flow\'u Takip Edin',
    step3Desc: 'Her ödeme durumu değişikliğinde ilgili endpoint\'e event gönderin:',
    
    // Authentication Section
    authTitle: 'Kimlik Doğrulama (API Key)',
    authUsageTitle: 'API Key Kullanımı',
    authUsageDesc: 'Tüm isteklerde Authorization header\'ında Bearer token olarak API key gönderilmelidir:',
    authTypesTitle: 'API Key Tipleri',
    authIpWhitelistTitle: 'IP Whitelist (Opsiyonel)',
    authIpWhitelistDesc: 'Güvenlik için API key\'inize belirli IP adreslerinden erişim kısıtlaması ekleyebilirsiniz:',
    
    // Table Headers
    tableEnvironment: 'Ortam',
    tablePrefix: 'Prefix',
    tableRateLimit: 'Rate Limit',
    tableUsage: 'Kullanım',
    tableTest: 'Test',
    tableProduction: 'Production',
    tableDevTest: 'Development ve test',
    tableProdEnv: 'Production ortamı',
    
    // Event Types Section
    eventTypesTitle: 'Event Tipleri',
    eventTypesDesc: 'Platform 8 farklı ödeme olayını destekler:',
    tableEvent: 'Event',
    tableEndpoint: 'Endpoint',
    tableWhenSent: 'Ne Zaman Gönderilir',
    
    eventInitializedWhen: 'Ödeme başlatıldığında (checkout page)',
    eventProviderRequestedWhen: 'Payment provider\'a istek gönderildiğinde',
    eventProviderSuccessWhen: 'Provider\'dan başarılı response alındığında',
    eventProviderFailedWhen: 'Provider\'dan hata alındığında',
    eventOrderCreatedWhen: 'Sipariş database\'e kaydedildiğinde',
    eventRevenueRecordedWhen: 'Gelir muhasebe sistemine kaydedildiğinde',
    eventRefundInitiatedWhen: 'İade başlatıldığında',
    eventRefundCompletedWhen: 'İade tamamlandığında',
    
    // Endpoints Section
    endpointsTitle: 'Endpoint\'ler',
    baseUrlTitle: 'Base URL',
    eventEndpointsTitle: 'Event Endpoint\'leri',
    batchEndpointTitle: 'Batch Endpoint',
    
    // JSON Formats Section
    jsonFormatsTitle: 'JSON Formatları',
    jsonFormatsDesc: 'Her event tipi için gerekli ve opsiyonel alanlar aşağıda detaylı olarak açıklanmıştır.',
    requiredFields: 'Zorunlu Alanlar',
    optionalFields: 'Opsiyonel Alanlar',
    fieldDescriptions: 'Alan Açıklamaları',
    exampleRequest: 'Örnek İstek',
    successResponse: 'Başarılı Yanıt',
    whenToSend: 'Ne Zaman:',
    
    // Payment Initialized
    paymentInitializedTitle: '1. Ödeme Başlatıldı',
    paymentInitializedWhen: 'Kullanıcı checkout sayfasına geldiğinde veya "Pay" butonuna tıkladığında.',
    
    // Provider Requested
    providerRequestedTitle: '2. Provider\'a İstek',
    providerRequestedWhen: 'Payment provider\'a (Stripe, Iyzico, vb.) ödeme isteği gönderildiğinde.',
    
    // Provider Success
    providerSuccessTitle: '3. Provider Başarılı',
    providerSuccessWhen: 'Payment provider\'dan başarılı response alındığında.',
    providerSuccessWarning: 'provider_payment_id alanı zorunludur. Bu alan provider\'dan dönen payment intent ID\'sini içermelidir (örn: Stripe\'da pi_abc123, Iyzico\'da paymentId). Bu ID webhook eşleştirmesi için kritiktir.',
    
    // Provider Failed
    providerFailedTitle: '4. Provider Başarısız',
    providerFailedWhen: 'Payment provider\'dan hata alındığında.',
    
    // Order Created
    orderCreatedTitle: '5. Sipariş Oluşturuldu',
    orderCreatedWhen: 'Sipariş database\'e kaydedildiğinde (başarılı ödeme sonrası).',
    
    // Revenue Recorded
    revenueRecordedTitle: '6. Gelir Kaydedildi',
    revenueRecordedWhen: 'Gelir muhasebe/accounting sistemine kaydedildiğinde.',
    
    // Refund Initiated
    refundInitiatedTitle: '7. İade Başlatıldı',
    refundInitiatedWhen: 'İade süreci başlatıldığında.',
    
    // Refund Completed
    refundCompletedTitle: '8. İade Tamamlandı',
    refundCompletedWhen: 'İade tamamlandığında ve para müşteriye iade edildiğinde.',
    
    // Batch Events Section
    batchEventsTitle: 'Toplu Eventler',
    batchEventsDesc: 'Birden fazla event\'i tek bir istekte göndermek için batch endpoint\'ini kullanın.',
    batchEventTypesMapping: 'Event Tipleri Eşleştirmesi',
    batchLimitsTitle: 'Limitler:',
    batchLimit1: 'Batch başına maksimum 100 event',
    batchLimit2: 'Maksimum payload boyutu: 1MB',
    
    // Error Handling Section
    errorHandlingTitle: 'Hata Yönetimi',
    httpStatusCodesTitle: 'HTTP Durum Kodları',
    tableCode: 'Kod',
    tableMeaning: 'Anlam',
    tableAction: 'Aksiyon',
    statusSuccess: 'Başarılı',
    statusSuccessAction: 'Event kaydedildi',
    statusBadRequest: 'Hatalı İstek',
    statusBadRequestAction: 'JSON formatı veya zorunlu alan eksik',
    statusUnauthorized: 'Yetkisiz',
    statusUnauthorizedAction: 'API key geçersiz veya eksik',
    statusValidationError: 'Doğrulama Hatası',
    statusValidationErrorAction: 'Data validation hatası',
    statusRateLimitExceeded: 'Rate Limit Aşıldı',
    statusRateLimitExceededAction: 'Rate limit aşıldı, tekrar deneyin',
    statusServerError: 'Sunucu Hatası',
    statusServerErrorAction: 'Platform hatası, tekrar deneyin',
    errorResponseFormat: 'Hata Yanıt Formatı',
    commonErrorCodes: 'Yaygın Hata Kodları',
    tableDescription: 'Açıklama',
    tableSolution: 'Çözüm',
    errorInvalidApiKey: 'API key geçersiz',
    errorInvalidApiKeySolution: 'API key\'i kontrol edin',
    errorValidation: 'Zorunlu alan eksik',
    errorValidationSolution: 'JSON\'ı kontrol edin',
    errorDuplicate: 'Event zaten gönderilmiş',
    errorDuplicateSolution: 'Idempotency key değiştirin',
    errorRateLimit: 'Rate limit aşıldı',
    errorRateLimitSolution: '60 saniye bekleyin',
    errorInvalidProvider: 'Desteklenmeyen provider',
    errorInvalidProviderSolution: 'Provider listesini kontrol edin',
    errorInvalidCurrency: 'Geçersiz para birimi',
    errorInvalidCurrencySolution: 'ISO 4217 kodları kullanın',
    retryStrategy: 'Yeniden Deneme Stratejisi',
    retryStrategyDesc: 'Exponential Backoff kullanarak retry mekanizması implementasyonu:',
    
    // Best Practices Section
    bestPracticesTitle: 'En İyi Pratikler',
    bp1Title: 'Idempotency Key\'ler',
    bp1Desc: 'Her event için benzersiz bir idempotency key kullanın:',
    bp1Why: 'Aynı event\'in yanlışlıkla 2 kez gönderilmesini önler.',
    bp1CodeComment: '// Örnek: init-order-12345-1704067200',
    bp2Title: 'Event Sırası',
    bp2Desc: 'Event\'leri doğru sırada gönderin:',
    bp2Why: 'Platform, state machine validation yapar. Yanlış sıra → risk detection.',
    bp3Title: 'Timestamp\'ler',
    bp3Desc: 'Event\'lerde timestamp veya recorded_at kullanın (ISO 8601):',
    bp3Why: 'Async processing detection için gerekli.',
    bp4Title: 'Tutar Tutarlılığı',
    bp4Desc: 'Tüm event\'lerde aynı tutarı kullanın:',
    bp4Why: 'Tutar uyuşmazlığı → risk detection.',
    bp5Title: 'Timeout Ayarları',
    bp5Desc: 'API call\'larına timeout ekleyin (önerilen: 10 saniye):',
    bp6Title: 'Metadata Kullanımı',
    bp6Desc: 'Debug için ek bilgi ekleyin:',
    why: 'Neden?',
    
    // Testing Section
    testingTitle: 'Test ve Debugging',
    testModeTitle: 'Test Modu',
    testModeDesc: 'Test API key kullanarak gerçek veri etkilemeden test edin:',
    testPaymentIdsTitle: 'Test Payment Intent ID\'leri',
    testPaymentIdsDesc: 'Test için özel ID\'ler:',
    testPatternSuccess: 'Her zaman başarılı',
    testPatternFail: 'Her zaman başarısız',
    testPatternTimeout: 'Timeout simülasyonu',
    dashboardCheckTitle: 'Dashboard\'da Kontrol',
    dashboardCheckDesc: 'Timeline\'da tüm event\'leri görebilirsiniz.',
    
    // Webhook Integration Section
    webhookTitle: 'Webhook Entegrasyonu ve Multi-Tenant Güvenlik',
    webhookCritical: 'Her Organizasyonun Unique Webhook URL\'i Var',
    webhookDesc: 'SilentLoss multi-tenant bir platformdur. Her müşterinizin (organization) kendi benzersiz webhook URL\'i vardır:',
    webhookUrlFormat: 'URL Formatı',
    securityLayersTitle: 'Güvenlik Katmanları',
    securityUrlIsolation: 'URL Tabanlı İzolasyon',
    securityUrlIsolationDesc: 'Her org farklı endpoint kullanır',
    securityWebhookSecret: 'Webhook Secret',
    securityWebhookSecretDesc: 'Her org\'un farklı secret\'ı olmalı',
    securitySignatureValidation: 'İmza Doğrulama',
    securitySignatureValidationDesc: 'Provider\'dan gelen her webhook doğrulanır',
    whyProviderPaymentId: 'Neden provider_payment_id Gerekli?',
    whyProviderPaymentIdDesc: 'SilentLoss Platform, hem SDK event\'leri hem de provider webhook\'larını işler. Bu iki kaynağı eşleştirmek için provider_payment_id alanı zorunludur:',
    webhookMatchingCode1: 'Sizin sistem:',
    webhookMatchingCode2: 'Stripe webhook:',
    webhookMatchingCode3: 'Bu ikisini eşleştirmek için provider_payment_id gerekli!',
    stripeWebhookSetupTitle: 'Stripe Webhook Kurulum Örneği',
    stripeStep1Title: 'Stripe Dashboard\'da Webhook Ekleyin',
    stripeStep2Title: 'Secret\'ı SilentLoss\'a Kaydedin',
    
    // SDKs Section
    sdksTitle: 'SDK\'lar ve Kütüphaneler',
    
    // Footer
    footerSupport: 'Destek',
    footerRateLimits: 'Rate Limitleri',
    footerSla: 'SLA',
    footerUptime: 'Uptime',
    footerResponseTime: 'Yanıt Süresi',
    footerSupportResponse: 'Destek Yanıtı',
    footerCopyright: 'SilentLoss Platform © 2026. Tüm hakları saklıdır.',
    
    // Common
    important: 'ÖNEMLİ',
    critical: 'KRİTİK',
    example: 'Örnek',
    type: 'Tip',
    required: 'Zorunlu',
    optional: 'Opsiyonel',
    field: 'Alan',
    uniquePaymentId: 'Benzersiz ödeme ID\'si',
    paymentAmount: 'Ödeme tutarı',
    currencyIso: 'Para birimi (ISO 4217)',
    paymentProvider: 'Payment provider',
    customerEmail: 'Müşteri email adresi',
    customerId: 'Müşteri ID\'si',
    productServiceId: 'Ürün/hizmet ID\'si',
    productServiceName: 'Ürün/hizmet adı',
    additionalInfo: 'Ek bilgiler',
    correspondingEndpoint: 'Karşılık Gelen Endpoint',
    apiVersion: 'API Versiyonu',
    
    // SDK Setup Section
    sdkSetupTitle: 'SDK Kurulumu (Dil Bazlı)',
    sdkSetupDesc: 'Fire & Forget: Tüm SDK örnekleri asenkron çalışır. Kullanıcınız asla beklemez, uygulamanız asla çökmez.',
    sdkJsTitle: 'JavaScript / Node.js (Önerilen)',
    sdkPhpTitle: 'PHP (Laravel ile)',
    sdkPhpOption1: 'Seçenek 1: Laravel Queue (Önerilen)',
    sdkPhpOption2: 'Seçenek 2: PHP-FPM (fastcgi_finish_request)',
    sdkPythonTitle: 'Python',
    sdkGoTitle: 'Go',
    sdkJavaTitle: 'Java',
    sdkCsharpTitle: 'C# / .NET',
    sdkRubyTitle: 'Ruby',
    sdkUsage: 'KULLANIM',
    sdkFireForgetNote: 'Bu satır ANINDA döner, kullanıcı beklemez!',
    
    // Example Scenarios Section
    examplesTitle: 'Örnek Senaryolar',
    examplesNote: 'Not: Tüm örneklerde paymentSafety SDK client\'ı Fire & Forget yaklaşımıyla çalışır. Hiçbir await kullanılmaz!',
    example1Title: 'Senaryo 1: Başarılı Stripe Ödemesi',
    example2Title: 'Senaryo 2: Başarısız Ödeme',
    example3Title: 'Senaryo 3: PHP Laravel ile Tam Örnek',
    
    // FAQ Section
    faqTitle: 'Sıkça Sorulan Sorular (SSS)',
    faq1Q: 'S: Event gönderimi başarısız olursa ne olur?',
    faq1A: 'Event\'ler arka planda retry edilir. Başarısız olsa bile ödeme işleminiz etkilenmez. Event\'ler loglanır ve daha sonra manuel olarak gönderilebilir.',
    faq2Q: 'S: Rate limit aşılırsa ne olur?',
    faq2A: '429 hatası alırsınız. Arka plan worker Retry-After header\'ına göre bekler ve tekrar dener. Ödeme işleminiz etkilenmez.',
    faq3Q: 'S: Tüm retry\'lar başarısız olursa?',
    faq3A: 'Event\'i logla ve bir "dead letter queue"ya kaydet. Daha sonra manuel olarak gönderebilirsiniz. Kritik: Ödeme işleminizi asla durdurmayın!',
    faq4Q: 'S: Senkron gönderim kullanabilir miyim?',
    faq4A: 'Önerilmez. Senkron gönderim şu riskleri taşır: API yavaşsa kullanıcı bekler (kötü UX), API hata verirse ödeme başarısız görünebilir (YANLIŞ!), Timeout olursa uygulamanız çökebilir',
    faq5Q: 'S: Hangi event\'ler zorunlu?',
    faq5A: 'Minimum: payment.initialized ve payment.provider_success. Tam koruma için tüm event\'leri gönderin.',
    faq6Q: 'S: Event sırası önemli mi?',
    faq6A: 'Evet! Yanlış sıra = potansiyel risk tespiti. Sıra: initialized → provider_requested → provider_success → order_created → revenue_recorded',
    
    // Updated Error Handling
    errorGoldenRule: 'ALTIN KURAL: SilentLoss API hatası, ödeme işleminizi ASLA durdurmamalıdır!',
    errorPaymentCritical: 'ÖDEMENİZ KRİTİK, ANALİTİK DEĞİL',
    errorPaymentCriticalDesc: 'Bu adım başarısız olsa bile sipariş OLUŞTURULMALIDIR!',
    errorDetectsNotCauses: 'SilentLoss kayıp gelir TESPİT eder. Kayıp gelire SEBEP olmaz.',
    yourApp: 'Uygulamanız',
    continueFlow: 'Devam et',
    logContinue: 'Logla, devam et',
    backgroundRetry: 'Arka planda retry',
    tableRetry: 'Retry?',
    yes: 'Evet',
    no: 'Hayır',
  }
};

export const t = (key, language) => {
  return translations[language]?.[key] || translations.en[key] || key;
};
